import {
  Accordion,
  ConfirmDialog,
  CustomButton,
  Modal,
  MultiLineInput,
  SelectDropDown,
  SvgCross,
  SvgDrag,
  SvgPencilEditIcon,
  SvgTrashIcon,
  SvgVerticalSplitter,
} from '@ui-kit'
import { cloneDeep, noop } from 'lodash'
import React, { useEffect, useState } from 'react'
import { BUTTONS, NAMESPACES } from '../utils/constants'
import { FieldValues, Path, PathValue, useWatch } from 'react-hook-form'
import { AdditionalNotesType } from '../types/layout.types'
import {
  DataTypeStore,
  EVENT_CONSTANTS,
  getFieldValues,
  hideLoader,
  showLoader,
} from '@app-store'
import { useAppDispatch } from '../utils/agent.store'
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd'

export const AdditionalNotes = <T extends FieldValues>({
  name,
  setValue,
  control,
  mode,
  ...props
}: AdditionalNotesType<T>) => {
  
  window.dispatchEvent(new Event(EVENT_CONSTANTS.DISABLE_FOCUS_TRAP))
  const value: string | undefined = useWatch({
    control: control,
    name: name as Path<T>,
  })
  const [localValue, setLocalValue] = useState<
    { heading: string; text: string; hasName: boolean }[]
  >([])
  const [openAddModal, setOpenAddModal] = useState(false)

  const getAccordionHeading = () => {
    return (
      <div className="flex justify-between items-center">
        <span>{props.label}</span>
        {mode !== 'view' ? (
          <div className="flex gap-4 pr-[0.28rem] items-center">
            <CustomButton
              widthClass="w-[3.25rem]"
              onClick={(e) => {
                e.stopPropagation()
                setOpenAddModal(true)
              }}
              className="!h-7"
              tabIndex={props?.tabIndex}
              onKeyDown={(e) => {
                e.stopPropagation()
                if (e?.key === 'Enter' || e?.key === ' ') setOpenAddModal(true)
              }}
            >
              {NAMESPACES.ADD}
            </CustomButton>
            <SvgVerticalSplitter />
          </div>
        ) : null}
      </div>
    )
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const reorderedNotes = Array.from(localValue)
    const [removed] = reorderedNotes.splice(result.source.index, 1)
    reorderedNotes.splice(result.destination.index, 0, removed)

    reorderedNotes.forEach((item, index) => {
      if (!item.hasName) {
        item.heading = `Notes ${index + 1}`
      }
    })

    const updatedString = reorderedNotes
      .map((item) => `${item.heading}:${item.text}`)
      .join('\n')

    setLocalValue(reorderedNotes)
    setValue?.(name, updatedString as PathValue<T, Path<T>>)
  }

  useEffect(() => {
    if (value?.length && !localValue.length) {
      const values = value.split('\n')
      const cloneValue = []
      for (let i = 0; i < values.length; i++) {
        const [label, note] = values[i].split(':', 2)
        cloneValue.push({
          heading: label ?? '',
          text: note ?? '',
          hasName: label !== `Notes ${i + 1}`,
        })
      }
      setLocalValue(cloneValue)
    }
  }, [value])
  return (
    <div>
      <Accordion
        heading={getAccordionHeading()}
        isDefaultActive={true}
        tabIndex={
          props?.tabIndex !== undefined ? props?.tabIndex - 1 : undefined
        }
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="notes-list">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {localValue.map((content, index) => {
                  return (
                    <Draggable
                      key={`${content?.heading}-${index}`}
                      draggableId={`note-${index}`}
                      index={index}
                      isDragDisabled={mode === 'view'}
                    >
                      {(provided) => (
                        <div
                          className="p-3"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <NoteBox
                            value={content.text}
                            handleChange={(
                              e: React.ChangeEvent<HTMLTextAreaElement>
                            ) => {
                              const updated = value?.split('\n') ?? []
                              const [label] = updated[index].split(':', 2)
                              updated[index] = `${label}:${e.target.value}`
                              setValue?.(
                                name,
                                updated.join('\n') as PathValue<T, Path<T>>
                              )
                              setLocalValue((prev) => {
                                const newPrev = [...prev]
                                newPrev[index].text = e.target.value
                                return newPrev
                              })
                            }}
                            onDelete={() => {
                              const updated = value?.split('\n') ?? []
                              updated.splice(index, 1)
                              const updatedLocal = cloneDeep(localValue)
                              updatedLocal.splice(index, 1)
                              updatedLocal.forEach((item, index) => {
                                item.heading = item.hasName
                                  ? item.heading
                                  : `Notes ${index + 1}`
                                item.hasName = item.hasName ?? false
                                updated[index] = `${item.heading}:${item.text}`
                              })
                              setValue?.(
                                name,
                                (updated.length > 0
                                  ? updated.join('\n')
                                  : '') as PathValue<T, Path<T>>
                              )
                              setLocalValue(updatedLocal)
                            }}
                            header={content?.heading || `Notes ${index + 1}`}
                            mode={mode}
                            tabIndex={(props?.tabIndex ?? 0) + index}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </div>
                      )}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Accordion>
      <Modal
        isOpen={openAddModal}
        closeModal={() => setOpenAddModal(false)}
        bodyCssClassName="min-w-[62rem]"
        showCloseButton={false}
      >
        <AddNotesModal
          key={openAddModal.toString()}
          onSave={(val, note_name) => {
            setOpenAddModal(false)
            let updated: string[] = []
            if (localValue?.length !== 0) updated = value?.split('\n') ?? []
            updated.unshift(`${note_name ?? ''}:${val}`)
            const updatedLocal = cloneDeep(localValue)
            updatedLocal.unshift({
              heading: note_name ?? ``,
              text: val,
              hasName: note_name !== '',
            })
            updatedLocal.forEach((item, index) => {
              item.heading = item.hasName ? item.heading : `Notes ${index + 1}`
              item.hasName = item.hasName ?? false
              updated[index] = `${item.heading}:${item.text}`
            })
            setValue?.(name, updated.join('\n') as PathValue<T, Path<T>>)
            setLocalValue(updatedLocal)
          }}
          onClose={() => setOpenAddModal(false)}
          label={props.label}
        />
      </Modal>
    </div>
  )
}

const NoteBox = (props: {
  header: string
  onDelete: () => void
  value: string
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  mode: 'add' | 'edit' | 'view'
  tabIndex?: number
  dragHandleProps?: DraggableProvidedDragHandleProps | null
}) => {
  const [isEditClicked, setIsEditClicked] = useState(false)
  const [modal, setModal] = useState<{
    message: string
    state: boolean
    onConfirm: () => void
  }>({
    message: '',
    state: false,
    onConfirm: noop,
  })
  const handleYesClick = () => {
    props.onDelete()
    setModal({
      state: false,
      message: '',
      onConfirm: noop,
    })
  }
  const handleTextAreaExpand = (
    event: React.FormEvent<HTMLTextAreaElement>
  ) => {
    if (event.target) {
      ;(event.target as HTMLElement).style.height = 'auto'
      ;(event.target as HTMLElement).style.height = `${
        (event.target as HTMLElement).scrollHeight
      }px`
    }
  }
  return (
    <div className="pr-3 py-2.5 pl-2.5 flex gap-2 flex-col border border-accent-100 rounded">
      <div className="flex justify-between pr-1">
        <span className="text-xs font-regular">{props.header}</span>

        {isEditClicked ? (
          <CustomButton
            widthClass="w-[3.5rem]"
            isFilled={true}
            onClick={() => setIsEditClicked(false)}
            className="!h-7"
            tabIndex={props?.tabIndex}
            onKeyDown={(e) => {
              if (e?.key === 'Enter' || e?.key === ' ') {
                setIsEditClicked(false)
              }
            }}
          >
            {BUTTONS.SAVE}
          </CustomButton>
        ) : (
          <div
            className={`flex gap-4 items-center ${
              props.mode === 'view' ? 'hidden' : ''
            }`}
          >
            <div className="cursor-pointer">
              <SvgPencilEditIcon
                width={20}
                height={20}
                onClick={() => setIsEditClicked(true)}
                tabIndex={props?.tabIndex}
                onKeyDown={(e) => {
                  if (e?.key === 'Enter' || e?.key === ' ') {
                    setIsEditClicked(true)
                  }
                }}
              />
            </div>
            <div className="cursor-pointer">
              <SvgTrashIcon
                width={20}
                height={20}
                onClick={() =>
                  setModal({
                    state: true,
                    message: NAMESPACES.CANCEL_THREAD_MSG,
                    onConfirm: handleYesClick,
                  })
                }
                onKeyDown={(e) => {
                  if (e?.key === 'Enter' || e?.key === ' ') {
                    e?.preventDefault()
                    setModal({
                      state: true,
                      message: NAMESPACES.CANCEL_THREAD_MSG,
                      onConfirm: handleYesClick,
                    })
                  }
                }}
                tabIndex={props?.tabIndex}
              />
            </div>
            <SvgVerticalSplitter />
            <div
              {...props.dragHandleProps}
              className="cursor-grab"
              tabIndex={props?.tabIndex}
            >
              <SvgDrag width={20} height={20} />
            </div>
          </div>
        )}
      </div>
      <div className="font-light text-xs pr-[5.56rem] break-words">
        {isEditClicked ? (
          <MultiLineInput
            name={``}
            value={props.value}
            onChange={(e) => {
              e.preventDefault()
              props.handleChange(e)
            }}
            onInput={handleTextAreaExpand}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
            rows={3}
            className="text-xs"
            widthClass=""
            autoSize={true}
            tabIndex={props?.tabIndex}
          >
            {props.value}
          </MultiLineInput>
        ) : (
          props.value
            .split('<br />')
            .map((item, index) => <div key={`${index}-${item}`}>{item}</div>)
        )}
      </div>
      <ConfirmDialog
        open={modal.state}
        onConfirm={modal.onConfirm}
        onCancel={() =>
          setModal({ state: false, message: '', onConfirm: noop })
        }
        message={modal.message}
      />
    </div>
  )
}

export const AddNotesModal = (props: {
  onSave: (val: string, note_name: string) => void
  onClose: () => void
  label?: string
}) => {
  const [textValue, setTextValue] = useState('')
  const fields = ['category', 'note_name', 'instructions', 'note_value']
  const [fieldValues, setFieldValues] = useState(
    fields.reduce((acc: { [key: string]: string }, field) => {
      acc[field] = ''
      return acc
    }, {})
  )
  const [dropdownOptions, setDropdownOptions] = useState(
    fields.reduce((acc: { [key: string]: [] }, field) => {
      acc[field] = []
      return acc
    }, {})
  )

  const dispatch = useAppDispatch()

  const handleFieldChange = async (index: number, value: string) => {
    try {
      setTextValue('')
      const cloneFieldValues = cloneDeep(fieldValues)
      const cloneDropdownOptions = cloneDeep(dropdownOptions)
      for (let i = 0; i < fields.length; i++) {
        if (i >= index) {
          cloneFieldValues[fields[i]] = ''
          cloneDropdownOptions[fields[i]] = []
        }
      }
      if (index && value) cloneFieldValues[fields[index - 1]] = value
      dispatch(showLoader())
      const data = await getFieldValues(
        'frequently_used_notes',
        fields[index],
        cloneFieldValues
      )
      cloneDropdownOptions[fields[index]] =
        data.data
          ?.filter((val: DataTypeStore) => val[fields[index]])
          ?.map((val: DataTypeStore) => ({
            label: val[fields[index]],
            value: val[fields[index]],
          })) ?? []
      setDropdownOptions(cloneDropdownOptions)
      setFieldValues(cloneFieldValues)
      dispatch(hideLoader())
    } catch (e) {
      console.log(e)
    }
  }
  useEffect(() => {
    handleFieldChange(0, '')
  }, [])
  return (
    <div className="flex flex-col gap-3 max-w-[62rem]">
      <div className="flex justify-between font-primary py-3">
        <span className="text-xs font-medium">{props.label}</span>
        <div className="cursor-pointer p-1" onClick={() => props.onClose()}>
          <span className="text-black">
            <SvgCross />
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 py-1.5">
        <SelectDropDown
          label="Category"
          options={dropdownOptions[fields[0]]}
          defaultValue={fieldValues[fields[0]]}
          name=""
          onChange={(e) => handleFieldChange(1, e.value)}
          isCrossable={false}
        />
        <SelectDropDown
          label="Note Name"
          options={dropdownOptions[fields[1]]}
          defaultValue={fieldValues[fields[1]]}
          name=""
          onChange={(e) => handleFieldChange(2, e.value)}
          isCrossable={false}
        />
      </div>
      <div className="grid grid-cols-2 gap-3 py-1.5">
        <SelectDropDown
          label="Instructions"
          options={dropdownOptions[fields[2]]}
          defaultValue={fieldValues[fields[2]]}
          name=""
          onChange={(e) => handleFieldChange(3, e.value)}
          isCrossable={false}
        />
        <SelectDropDown
          label="Note Value"
          options={dropdownOptions[fields[3]]}
          defaultValue={fieldValues[fields[3]]}
          name=""
          onChange={(e) => {
            const cloneFieldValues = cloneDeep(fieldValues)
            cloneFieldValues[fields[3]] = e.value
            setFieldValues(cloneFieldValues)
            setTextValue(e.value)
          }}
          isCrossable={false}
        />
      </div>
      <hr />
      <MultiLineInput
        name={''}
        value={textValue}
        onChange={(e) => {
          setTextValue(e.target.value)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
          }
        }}
        label="Notes"
        className="!my-1.5"
        disabled={!fieldValues[fields[3]]}
      />

      <div className="flex gap-2 justify-end">
        <CustomButton widthClass="w-[4.3rem]" onClick={() => props.onClose()}>
          {BUTTONS.CANCEL}
        </CustomButton>
        <CustomButton
          isFilled={true}
          widthClass="w-[3.5rem]"
          onClick={() => {
            if (textValue) props.onSave(textValue, fieldValues.note_name)
            else props.onClose()
          }}
        >
          {BUTTONS.SAVE}
        </CustomButton>
      </div>
    </div>
  )
}
